import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { ValidationContext, ValidationContextProps } from '@e2m-customer-portal/hooks';
import { ValidationMode } from '@e2m-customer-portal/shared';

export interface ValidatedContainerProps {
  /**
   * Validation mode. Used by inputs of this container to control their behaviour.
   */
  validate: ValidationMode;

  /**
   * Gets triggered whenever the validity of the inputs of this container changes.
   * @param valid
   */
  onValidated: (valid: boolean) => void;

  /**
   * Child elements of this container. All validated inputs within the children are part of this validation
   * context.
   */
  children?: ReactNode;
  /**
   * A value that work as a reset signal for validation state.
   */
  resetValidationTrigger?: unknown;
}

/**
 * @summary A validated container that acts as a ValidationContext for all children that are validated inputs.
 *
 * A validated container that acts as a ValidationContext for all children that are validated inputs. This container
 * controls the validation mode of the validated inputs and reports if all of them are valid.
 *
 * @example
 * function MyComponent() {
 *   const [validationMode, setValidationMode] = useState('blur');
 *   const [isValid, setIsValid] = useState(false);
 *
 *   const submit = () => {
 *     setValidationMode('on');
 *     if (isValid) {
 *       // Will only be called when the username field is filled in. Setting the validation mode above will
 *       // highlight any fields that are still invalid.
 *     }
 *   };
 *
 *   return (
 *     <ValidatedContainer validate={validationMode} onValidated={setIsValid}>
 *       <MyInput name="username" rules={[isRequired()]} />
 *       <MyButton onClick={submit} />
 *     </ValidatedContainer>
 *   );
 * }
 */
export function ValidatedContainer({
  validate,
  onValidated,
  children,
  resetValidationTrigger,
}: Readonly<ValidatedContainerProps>): ReactElement<ValidatedContainerProps> {
  const [childStates, setChildStates] = useState(new Map());
  const [reportedValidity, setReportedValidity] = useState<boolean>();

  useEffect(() => {
    setChildStates(new Map());
  }, [resetValidationTrigger]);

  const context = useMemo(
    () =>
      ({
        mode: validate,
        onChange: (id, valid) => {
          childStates.set(id, valid);
          const allValid = Array.from(childStates.values()).every(Boolean);
          if (allValid !== reportedValidity) {
            setReportedValidity(allValid);
            onValidated(allValid);
          }
        },
      }) satisfies ValidationContextProps,
    [childStates, reportedValidity, validate, onValidated, resetValidationTrigger],
  );

  return <ValidationContext.Provider value={context}>{children}</ValidationContext.Provider>;
}
